import React, { useEffect, useState } from "react";
import { MdDeleteForever } from "react-icons/md";
import { RiErrorWarningFill, RiUploadLine } from "react-icons/ri";
import { LineWave } from "react-loader-spinner";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { useDeleteExerciseMutation } from "src/assets/queries/exerciseTemplates/mutations";
import { ExercisesIcon, ExercisesIconEdit } from "src/assets/static/icons";
import Inputfield from "src/components/inputfield";
import WorkingWarn from "src/components/WorkingWarn";
import { useDialog } from "src/contexts/dialog-context";
import convertYouTubeLinkToEmbed, {
  isYTLinkValid,
} from "src/modules/fithub-shared/functions/youtube-utils";
import { ExerciseTemplate } from "src/modules/fithub-shared/interfaces/templates";
import MuscleSerie from "src/modules/templates/components/MuscleSerie";
import SelectButtons from "src/modules/templates/components/SelectButtons";
import api from "src/rest/api";
import styled from "styled-components";

export const ExerciseInfo = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  border: 1px solid #ddd;
  padding: 10px;

  width: 100%;
  max-width: 1000px;

  align-items: start;
  justify-content: start;

  :not(:last-child) {
    margin-bottom: 10px;
  }
`;

export const NameHeader = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  font-size: 32px;

  align-items: center;
`;

export const EditableLabel = styled.input<{ disabled?: boolean }>`
  display: flex;
  box-shadow: none;
  flex: 1;

  ${(p) => !p.disabled && "text-decoration: underline lightgrey;"}
`;

export const Section = styled.div<{ disabled?: boolean }>`
  display: flex;
  font-size: 16px;

  ${(p) => p.disabled && "font-style: italic;"}
  ${(p) => p.disabled && "color: #888;"}

    padding: 5px;
  border-radius: 5px;

  > input {
    text-align: right;
    font-size: 16px;
    background-color: unset;
  }

  align-items: center;
  width: 100%;
  justify-content: space-between;

  :nth-child(odd) {
    background-color: #eee;
  }
`;

export const Footer = styled.div`
  display: flex;
  justify-content: center;
  padding-block: 10px;

  > div {
    justify-content: center;
    align-items: center;
    flex: 0;

    button {
      height: 40px;

      p {
        margin-left: 10px;
        font-weight: bold;
      }

      justify-content: space-between;

      background-color: var(--primary);
      padding: 8px;
      border-radius: 8px;
      align-items: center;

      color: white;

      :not(:disabled) {
        :active {
          opacity: 0.75;
          transform: scale(0.9);
        }
        cursor: pointer;
      }

      :disabled {
        background-color: #ddd;
        color: #888;
      }
    }
  }
`;

export const Media = styled.div`
  display: flex;
  width: 30%;
  min-width: 350px;

  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 10px;

  > iframe,
  .error {
    width: 100%;
    aspect-ratio: calc(16 / 9);
    box-shadow: none;
    border-radius: 5px;
    outline: none;
    border: 1px solid #eee;
    margin-bottom: 5px;
  }
`;

export const Div = styled.div`
  display: flex;
  align-items: center;

  flex: 1;

  background-color: cyan;

  > div {
    flex-direction: column;
    align-items: center;
  }
`;

export const CenteredDiv = styled.div`
  display: flex;

  flex-direction: column;
  flex: 1;
  justify-content: center;
  align-items: center;

  > div {
    flex-direction: column;
    align-items: center;
  }
`;

const ExerciseDetails: React.FC<{
  id: string | undefined;
  reloadList: () => void;
}> = ({ id, reloadList }) => {
  const [exercise, setExercise] = useState<ExerciseTemplate>();
  const [modified, setModified] = useState(false);

  useEffect(() => {
    setExercise(undefined);
  }, [id]);

  function onChangeData(e: React.ChangeEvent<HTMLInputElement>) {
    if (!exercise) return;

    setModified(true);

    const { name, value } = e.target;

    setExercise({
      ...exercise,
      [name]: value,
    });
  }

  function onChangeMode(v: string) {
    if (!exercise) return;
    setExercise({
      ...exercise,
      mode: v,
    });
    setModified(true);
  }

  function onChangeJoint(v: string) {
    if (!exercise) return;
    setExercise({
      ...exercise,
      joint: v,
    });
    setModified(true);
  }

  function onChangeProfile(v: string) {
    if (!exercise) return;
    setExercise({
      ...exercise,
      profile: v,
    });

    setModified(true);
  }

  function onBlurMedia(e: React.FocusEvent<HTMLInputElement>) {
    const { name, value } = e.target;

    if (!exercise || exercise.media_url === value) return;

    setModified(true);

    setExercise({
      ...exercise,
      [name]: value,
    });
  }

  const { isLoading } = useQuery(["exc", id], () =>
    api
      .post<ExerciseTemplate>("/exercises/get", {
        id,
      })
      .then((res) => {
        if (!modified || exercise?.id != res.data.id) {
          setTimeout(() => {
            setExercise(res.data);
          }, 100);
          setModified(false);
        }
        return res.data;
      }),
  );

  function ApplyEdit() {
    if (!exercise) return;

    api
      .post<ExerciseTemplate>("/exercises/edit", {
        ...exercise,
      })
      .then((res) => {
        setExercise({ ...res.data, editable: true });
        setModified(false);
        reloadList();
      })
      .catch((err) => alert(err));
  }

  const dialog = useDialog();

  const exerciseDelete = useDeleteExerciseMutation();

  const nav = useNavigate();

  function Delete() {
    dialog.openDialog(
      "Excluir Exercício",
      `Atenção!
            excluir um exercício pode invalidar diversos outros dados da prescrição.
            \nTem certeza que deseja excluir o exercício "${exercise?.name}" de sua lista?`,
      () => {
        exerciseDelete.mutateAsync(exercise?.id);
        nav("/tools/exercises");
      },
    );
  }

  if (!id)
    return (
      <div className="flex flex-1 flex-col items-center justify-center">
        <div className="flex flex-col items-center">
          <ExercisesIcon size={48} />
          <span style={{ margin: 5 }} />
          <p>Selecione um Exercício!</p>
        </div>
      </div>
    );
  else if (isLoading || !exercise) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center">
        <LineWave width={60} height={60} color="black" />
      </div>
    );
  } else {
    return (
      <div className="flex flex-col">
        <div className="mx-auto mb-2 flex w-full max-w-7xl flex-col items-start justify-start rounded-lg border border-gray-300 p-2">
          <div className="flex w-full flex-col items-center pb-5 text-2xl">
            <h4 className="text-xl">Exercício Template</h4>
            <p className="text-sm">Editar ou remover Exercício predefinido.</p>
          </div>

          <div className="flex w-full items-start pb-5">
            <div className="flex h-full flex-1 flex-col flex-wrap">
              <div className="flex flex-row justify-between py-2">
                <div className="flex w-full flex-row items-center">
                  <ExercisesIconEdit size={24} />
                  <input
                    name={"name"}
                    disabled={!exercise.editable}
                    value={exercise.name}
                    onChange={onChangeData}
                    className={`flex-1 border-none bg-transparent text-xl shadow-none outline-none ${!exercise.editable ? "" : "underline decoration-gray-300"}`}
                  />
                </div>
                {exercise.editable && (
                  <div className="flex justify-center py-2">
                    <div className="flex items-center">
                      <button
                        disabled={!modified}
                        onClick={ApplyEdit}
                        className="bg-primary flex h-10 items-center justify-between rounded-lg bg-primary-500 px-2 font-bold text-white active:scale-90 active:opacity-75 disabled:bg-gray-300 disabled:text-gray-400"
                      >
                        <RiUploadLine />
                        <p className="ml-2">Salvar</p>
                      </button>
                      <button
                        className="bg-primary ml-2 flex h-10 items-center justify-between rounded-lg bg-primary-500 px-2 font-bold text-white active:scale-90 active:opacity-75"
                        onClick={Delete}
                      >
                        <MdDeleteForever />
                        <p className="ml-2">Excluir</p>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-1 flex-row items-center justify-between">
                <div className="flex flex-col">
                  <SelectButtons
                    title={"Tipo"}
                    buttons={[{ title: "LIVRE" }, { title: "MAQUINA" }]}
                    onSelect={onChangeMode}
                    defaultValue={exercise.mode}
                    disabled={!exercise.editable}
                  />

                  <SelectButtons
                    title={"Articulação"}
                    buttons={[{ title: "MONO" }, { title: "MULTI" }]}
                    onSelect={onChangeJoint}
                    defaultValue={exercise.joint}
                    disabled={!exercise.editable}
                  />

                  <SelectButtons
                    title={"Perfil de Resistência"}
                    buttons={[
                      { title: "ASCENDENTE", value: "ASC" },
                      { title: "DESCENDENTE", value: "DESC" },
                      { title: "U INVERTIDO", value: "INV" },
                    ]}
                    onSelect={onChangeProfile}
                    defaultValue={exercise.profile}
                    disabled={!exercise.editable}
                  />
                </div>

                <div className="flex w-[30%] min-w-[350px] flex-col rounded-md border border-gray-300 p-2">
                  {exercise.media_url.includes("youtube") ? (
                    <iframe
                      className="mb-1 aspect-video w-full rounded border border-gray-200 shadow"
                      src={`${convertYouTubeLinkToEmbed(
                        exercise.media_url,
                      )}?modestbranding=1&rel=0&showinfo=0&controls=1`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <div className="mb-1 flex aspect-video w-full flex-col items-center justify-center rounded border border-gray-200 text-red-500">
                      <RiErrorWarningFill />
                      <p>Link Inválido!</p>
                    </div>
                  )}
                  <Inputfield
                    inputProps={{
                      name: "media_url",
                      disabled: !exercise.editable,
                      value: exercise.media_url,
                      onChange: onChangeData,
                      onBlur: onBlurMedia,
                    }}
                    error={!isYTLinkValid(exercise.media_url)}
                    hint="URL Video"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mx-auto mb-2 flex w-full max-w-7xl flex-col items-start justify-start rounded-lg border border-gray-300 p-2">
          <div className="flex flex-col pb-5">
            <h4 className="text-xl">Exercícios de Substituição</h4>
            <p className="text-sm">
              Listagem de exercícios alternativos, caso o aluno não consiga
              executar o primário.
            </p>
          </div>
          {/*<ExercisesReplacement id={id} />*/}
          <WorkingWarn fun />
        </div>

        <div className="mx-auto flex w-full max-w-7xl flex-col items-start justify-start rounded-lg border border-gray-300 p-2">
          <div className="flex flex-col pb-5">
            <h4 className="text-xl">Volume de Treino</h4>
            <p className="text-sm">
              Listagem de cálculo de volume, contabilizado X séries para músculo
              Y.
            </p>
          </div>
          <div className="w-full">
            <MuscleSerie />
          </div>
        </div>
      </div>
    );
  }
};

export default ExerciseDetails;
